import ImageUploadPattern from 'assets/images/ImageUploadPattern.png';

import { memo, forwardRef, Fragment, useEffect, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
import useMenu from 'hooks/useMenu';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectAIModuleFeatureEnable } from 'store/currentUser/currentUserSelectors';
// Types
import MediaFormats from 'types/MediaFormats';
// Icons
import { FaMagic, FaEllipsisV, FaCheckCircle, FaTimes } from 'react-icons/fa';
// Components
import { Button, IconButton } from 'components/Buttons';
import MediaDialog from 'dialogs/Media.dialog';
import GenerateImageDialog from 'dialogs/GenerateImage.dialog';
import { mediasInfo } from 'utilities/utilities';

type Props = {
  className?: string;
  label?: string;
  value: string;
  onChange: (imageUrl:string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  format: MediaFormats;
  backgroundColor?: string;
}

const BANNER_DURATION = 4000;

type PreviousState = { value: string; isAiGenerated: boolean };

const ImageUpload = forwardRef<any, Props>(({
  // Props
  className = '',
  label = '', value, onChange, required = false, disabled = false,
  error = false, errorText = '', helperText = '',
  format, backgroundColor, ...otherProps
}, ref) => {
  const { t } = useTranslation();
  const aiModuleEnabled = useAppSelector(selectAIModuleFeatureEnable);

  const [previous, setPrevious] = useState<PreviousState | null>(null);
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!bannerMessage) return;
    const timer = setTimeout(() => setBannerMessage(null), BANNER_DURATION);
    return () => clearTimeout(timer);
  }, [bannerMessage]);

  const commitChange = (newValue: string, aiGenerated: boolean) => {
    setPrevious({ value, isAiGenerated });
    setIsAiGenerated(aiGenerated);
    onChange(newValue);
    const resolvedLabel = label ? t(label) : t('components.imageUpload.defaultLabel');
    setBannerMessage(t('components.imageUpload.updatedMessage', { label: resolvedLabel }));
  }

  const handleMediaSelected = (imageUrl: string) => commitChange(imageUrl, false);
  const handleApplyGeneratedImage = (imageUrl: string) => commitChange(imageUrl, true);

  const handleClickClear = () => {
    setPrevious({ value, isAiGenerated });
    setIsAiGenerated(false);
    setBannerMessage(null);
    onChange('');
  }

  const handleRestorePrevious = () => {
    if (!previous) return;
    onChange(previous.value);
    setIsAiGenerated(previous.isAiGenerated);
    setPrevious(null);
    setBannerMessage(null);
  }

  const width = mediasInfo[format].width;
  const height = mediasInfo[format].height;

  const paddingTop = height/width * 100;
  const hasImage = Boolean(value);

  const { open, toggle } = useToggle();
  const [openAi, setOpenAi] = useState(false);
  const { Menu, MenuItem, openMenu } = useMenu();

  return (
    <Fragment>
      {open ? (
        <MediaDialog open={open} onClose={toggle} type="image" onChange={aiModuleEnabled ? handleMediaSelected : onChange} initialFormat={format} />
      ) : null}

      {aiModuleEnabled && openAi ? (
        <GenerateImageDialog
          open={openAi}
          onClose={() => setOpenAi(false)}
          format={format}
          onApply={handleApplyGeneratedImage}
        />
      ) : null}

      <div className="mb-4">
        <input ref={ref} type="hidden" name={(otherProps as any).name} />

        {aiModuleEnabled && bannerMessage ? (
          <div className="flex items-center justify-between gap-3 mb-2 px-3 py-2 rounded bg-green-600 max-w-lg">
            <span className="flex items-center gap-2 text-white text-sm font-medium">
              <FaCheckCircle className="flex-shrink-0" />{bannerMessage}
            </span>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                className="text-white text-sm font-semibold underline underline-offset-2"
                onClick={handleRestorePrevious}
              >{t('buttons.undo')}</button>
              <button
                type="button"
                className="text-white text-opacity-80 hover:text-opacity-100"
                onClick={() => setBannerMessage(null)}
                aria-label={t('buttons.close')}
              ><FaTimes size={14} /></button>
            </div>
          </div>
        ) : null}

        <div className={className}>
          {label ? (
            <span className={`block text-white text-sm font-semibold mb-1 ${disabled ? 'opacity-50' : ''}`}>
              {t(label)}{required ? <Fragment>&nbsp;*</Fragment> : null}
            </span>
          ) : null}

          <div
            className={`
              relative overflow-hidden w-full mb-4
              ${format === MediaFormats.HomeScreen ? 'rounded-full' : 'rounded'}
              ${!hasImage ? 'border-2 border-dashed border-gray-600' : ''}
            `}
            style={{
              backgroundImage: `url(${ImageUploadPattern})`,
              boxShadow: 'inset 0 0 16px rgba(0,0,0,0.75)',
              paddingTop: `${paddingTop}%`
            }}
          >
            {hasImage ? (
              <div
                className="absolute top-0 left-0 w-full h-full"
                style={{ backgroundColor: backgroundColor ?? 'transparent' }}
              >
                <img
                  className="w-full h-full object-contain"
                  src={value}
                  alt=""
                />
                {aiModuleEnabled && isAiGenerated ? (
                  <span className="absolute top-2 left-2 flex items-center gap-1 bg-black bg-opacity-60 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                    <FaMagic size={10} />{t('components.imageUpload.aiBadge')}
                  </span>
                ) : null}
              </div>
            ) : (
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center px-4">
                <span className="bg-black bg-opacity-50 text-gray-200 text-sm font-light text-center px-3 py-1.5 rounded">
                  {t('components.imageUpload.emptyStateText')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outlined"
            disabled={disabled}
            onClick={toggle}
          >{t('buttons.upload')}</Button>
          {aiModuleEnabled ? (
            <Fragment>
              <Button
                variant="outlined"
                disabled={disabled}
                onClick={() => setOpenAi(true)}
              ><FaMagic className="inline-block mr-2 -mt-0.5" />{t('buttons.generateWithAi')}</Button>
              <IconButton onClick={openMenu} disabled={disabled} aria-label={t('buttons.more')}>
                <FaEllipsisV />
              </IconButton>
              <Menu>
                <MenuItem onClick={handleClickClear} disabled={!hasImage}>{t('components.imageUpload.clearMenuItem')}</MenuItem>
                {previous ? (
                  <MenuItem onClick={handleRestorePrevious}>{t('buttons.restorePrevious')}</MenuItem>
                ) : null}
              </Menu>
            </Fragment>
          ) : (
            <Button
              variant="outlined"
              disabled={disabled || !hasImage}
              onClick={() => onChange('')}
            >{t('buttons.clear')}</Button>
          )}
        </div>
        <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{mediasInfo[format].hint}</span>
        {helperText && helperText ? <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{t(helperText)}</span> : null}
        {error && errorText ? <span className="block text-red-500 text-sm py-1 px-4">{t(errorText)}</span> : null}
      </div>
    </Fragment>
  )
});

export default memo(ImageUpload, (prev, next) => {
  return prev.value === next.value && prev.error === next.error && prev.disabled === next.disabled
});
